// IndexedDB存储管理器实现

import type {
  StorageManager,
  StorageInfo,
  StorageConfig,
  StorageEvent,
  StorageEventType
} from './storage';

import type {
  BookSource,
  BookInfo,
  UserDataType,
  CacheType
} from '@/types/reading';

import { DEFAULT_STORAGE_CONFIG } from './storage';

export class IndexedDBManager implements StorageManager {
  private db: IDBDatabase | null = null;
  private config: StorageConfig;
  private eventListeners: Map<StorageEventType, Function[]> = new Map();

  constructor(config: StorageConfig = DEFAULT_STORAGE_CONFIG) {
    this.config = config;
  }

  // ============================================================================
  // 初始化和配置
  // ============================================================================

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.databaseName, this.config.databaseVersion);

      request.onerror = () => {
        reject(new Error(`Failed to open database: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.setupErrorHandling();
        this.emitEvent('INITIALIZED', { version: this.config.databaseVersion });
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createObjectStores(db);
      };
    });
  }

  async clear(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['bookSources', 'books', 'userData', 'cache'], 'readwrite');
    
    await Promise.all([
      this.clearObjectStore(transaction, 'bookSources'),
      this.clearObjectStore(transaction, 'books'),
      this.clearObjectStore(transaction, 'userData'),
      this.clearObjectStore(transaction, 'cache')
    ]);
  }

  async getStorageInfo(): Promise<StorageInfo> {
    if (!this.db) throw new Error('Database not initialized');

    const estimate = await navigator.storage?.estimate();
    const cacheInfo = await this.getCacheInfo();

    return {
      indexedDB: {
        used: estimate?.usage || 0,
        available: (estimate?.quota || 0) - (estimate?.usage || 0),
        quota: estimate?.quota || 0
      },
      localStorage: {
        used: this.getLocalStorageSize(),
        available: 10 * 1024 * 1024 - this.getLocalStorageSize(), // 假设10MB限制
        quota: 10 * 1024 * 1024
      },
      cache: cacheInfo
    };
  }

  // ============================================================================
  // 书源存储
  // ============================================================================

  async saveBookSource(source: BookSource): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['bookSources'], 'readwrite');
    const store = transaction.objectStore('bookSources');

    const bookSourceStore = {
      id: source.id,
      name: source.bookSourceName,
      url: source.bookSourceUrl,
      config: source,
      enabled: source.enabled,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(bookSourceStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save book source: ${request.error?.message}`));
    });
  }

  async getBookSource(id: string): Promise<BookSource | null> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['bookSources'], 'readonly');
    const store = transaction.objectStore('bookSources');

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.config : null);
      };
      request.onerror = () => reject(new Error(`Failed to get book source: ${request.error?.message}`));
    });
  }

  async getBookSources(): Promise<BookSource[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['bookSources'], 'readonly');
    const store = transaction.objectStore('bookSources');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result || [];
        resolve(results.map(item => item.config));
      };
      request.onerror = () => reject(new Error(`Failed to get book sources: ${request.error?.message}`));
    });
  }

  async updateBookSource(id: string, updates: Partial<BookSource>): Promise<void> {
    const existing = await this.getBookSource(id);
    if (!existing) throw new Error('Book source not found');

    const updated = { ...existing, ...updates };
    await this.saveBookSource(updated);
  }

  async deleteBookSource(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['bookSources'], 'readwrite');
    const store = transaction.objectStore('bookSources');

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete book source: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 图书信息存储
  // ============================================================================

  async saveBookInfo(book: BookInfo): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readwrite');
    const store = transaction.objectStore('books');

    const bookInfoStore = {
      id: book.id,
      sourceId: book.sourceId,
      title: book.title,
      author: book.author,
      category: book.category,
      coverUrl: book.coverUrl,
      description: book.description,
      chapters: book.chapters,
      metadata: {
        tags: book.tags,
        lastChapter: book.lastChapter,
        updateTime: book.updateTime,
        wordCount: book.wordCount,
        rating: book.rating,
        status: book.status,
        language: book.language
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(bookInfoStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save book info: ${request.error?.message}`));
    });
  }

  async getBookInfo(id: string): Promise<BookInfo | null> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readonly');
    const store = transaction.objectStore('books');

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        const bookInfo: BookInfo = {
          id: result.id,
          title: result.title,
          author: result.author,
          category: result.category,
          coverUrl: result.coverUrl,
          description: result.description,
          chapters: result.chapters,
          sourceId: result.sourceId,
          sourceUrl: '', // 需要从书源中获取
          tags: result.metadata.tags || [],
          lastChapter: result.metadata.lastChapter,
          updateTime: result.metadata.updateTime,
          wordCount: result.metadata.wordCount,
          rating: result.metadata.rating,
          status: result.metadata.status,
          language: result.metadata.language
        };

        resolve(bookInfo);
      };
      request.onerror = () => reject(new Error(`Failed to get book info: ${request.error?.message}`));
    });
  }

  async getBooksByCategory(category: string): Promise<BookInfo[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readonly');
    const store = transaction.objectStore('books');
    const index = store.index('category');

    return new Promise((resolve, reject) => {
      const request = index.getAll(category);
      request.onsuccess = () => {
        const results = request.result || [];
        const books = results.map(result => this.convertToBookInfo(result));
        resolve(books);
      };
      request.onerror = () => reject(new Error(`Failed to get books by category: ${request.error?.message}`));
    });
  }

  async getBooksBySource(sourceId: string): Promise<BookInfo[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readonly');
    const store = transaction.objectStore('books');
    const index = store.index('sourceId');

    return new Promise((resolve, reject) => {
      const request = index.getAll(sourceId);
      request.onsuccess = () => {
        const results = request.result || [];
        const books = results.map(result => this.convertToBookInfo(result));
        resolve(books);
      };
      request.onerror = () => reject(new Error(`Failed to get books by source: ${request.error?.message}`));
    });
  }

  async searchBooks(query: string): Promise<BookInfo[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readonly');
    const store = transaction.objectStore('books');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result || [];
        const queryLower = query.toLowerCase();
        
        const filteredBooks = results
          .filter(result => 
            result.title.toLowerCase().includes(queryLower) ||
            result.author.toLowerCase().includes(queryLower) ||
            (result.description && result.description.toLowerCase().includes(queryLower))
          )
          .map(result => this.convertToBookInfo(result));

        resolve(filteredBooks);
      };
      request.onerror = () => reject(new Error(`Failed to search books: ${request.error?.message}`));
    });
  }

  async getAllBookInfo(): Promise<BookInfo[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readonly');
    const store = transaction.objectStore('books');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result || [];
        const books = results.map(result => this.convertToBookInfo(result));
        resolve(books);
      };
      request.onerror = () => reject(new Error(`Failed to get all book info: ${request.error?.message}`));
    });
  }

  async deleteBookInfo(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['books'], 'readwrite');
    const store = transaction.objectStore('books');

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete book info: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 用户数据存储
  // ============================================================================

  async saveUserData(type: UserDataType, data: any, bookId?: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');

    const userDataStore = {
      id: this.generateUserDataId(type, bookId),
      type,
      bookId,
      data,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(userDataStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save user data: ${request.error?.message}`));
    });
  }

  async getUserData(type: UserDataType, bookId?: string): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const id = this.generateUserDataId(type, bookId);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(new Error(`Failed to get user data: ${request.error?.message}`));
    });
  }

  async deleteUserData(type: UserDataType, bookId?: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');
    const id = this.generateUserDataId(type, bookId);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete user data: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 用户图书数据存储
  // ============================================================================

  async saveUserBookData(data: import('@/types/reading').UserBookData): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');

    const userBookDataStore = {
      id: `userBookData_${data.bookId}`,
      type: 'userBookData' as UserDataType,
      bookId: data.bookId,
      data,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(userBookDataStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save user book data: ${request.error?.message}`));
    });
  }

  async getUserBookData(bookId: string): Promise<import('@/types/reading').UserBookData | null> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const id = `userBookData_${bookId}`;

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(new Error(`Failed to get user book data: ${request.error?.message}`));
    });
  }

  async getAllUserBookData(): Promise<import('@/types/reading').UserBookData[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const index = store.index('type');

    return new Promise((resolve, reject) => {
      const request = index.getAll('userBookData');
      request.onsuccess = () => {
        const results = request.result || [];
        resolve(results.map(result => result.data));
      };
      request.onerror = () => reject(new Error(`Failed to get all user book data: ${request.error?.message}`));
    });
  }

  async deleteUserBookData(bookId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');
    const id = `userBookData_${bookId}`;

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete user book data: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 图书集合存储
  // ============================================================================

  async saveBookCollection(collection: import('@/types/reading').BookCollection): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');

    const collectionStore = {
      id: `collection_${collection.id}`,
      type: 'collection' as UserDataType,
      bookId: undefined,
      data: collection,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(collectionStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save book collection: ${request.error?.message}`));
    });
  }

  async getBookCollection(id: string): Promise<import('@/types/reading').BookCollection | null> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const collectionId = `collection_${id}`;

    return new Promise((resolve, reject) => {
      const request = store.get(collectionId);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(new Error(`Failed to get book collection: ${request.error?.message}`));
    });
  }

  async getAllBookCollections(): Promise<import('@/types/reading').BookCollection[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const index = store.index('type');

    return new Promise((resolve, reject) => {
      const request = index.getAll('collection');
      request.onsuccess = () => {
        const results = request.result || [];
        resolve(results.map(result => result.data));
      };
      request.onerror = () => reject(new Error(`Failed to get all book collections: ${request.error?.message}`));
    });
  }

  async deleteBookCollection(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');
    const collectionId = `collection_${id}`;

    return new Promise((resolve, reject) => {
      const request = store.delete(collectionId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete book collection: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 搜索历史存储
  // ============================================================================

  async saveSearchHistory(history: import('@/types/reading').SearchHistory[]): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');

    const historyStore = {
      id: 'searchHistory',
      type: 'searchHistory' as UserDataType,
      bookId: undefined,
      data: history,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(historyStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save search history: ${request.error?.message}`));
    });
  }

  async getSearchHistory(): Promise<import('@/types/reading').SearchHistory[]> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');

    return new Promise((resolve, reject) => {
      const request = store.get('searchHistory');
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : []);
      };
      request.onerror = () => reject(new Error(`Failed to get search history: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 缓存管理
  // ============================================================================

  async setCacheItem(type: CacheType, key: string, data: any, ttl: number): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');

    const cacheStore = {
      id: `${type}:${key}`,
      type,
      key,
      data,
      expiresAt: new Date(Date.now() + ttl * 60 * 60 * 1000), // ttl in hours
      createdAt: new Date()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cacheStore);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to set cache item: ${request.error?.message}`));
    });
  }

  async getCacheItem(type: CacheType, key: string): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['cache'], 'readonly');
    const store = transaction.objectStore('cache');
    const id = `${type}:${key}`;

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        // 检查是否过期
        if (new Date() > result.expiresAt) {
          // 异步删除过期项
          this.deleteCacheItem(id);
          resolve(null);
          return;
        }

        resolve(result.data);
      };
      request.onerror = () => reject(new Error(`Failed to get cache item: ${request.error?.message}`));
    });
  }

  async clearExpiredCache(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    const index = store.index('expiresAt');
    const now = new Date();

    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(now));
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          this.emitEvent('CACHE_CLEANED', { deletedCount });
          resolve();
        }
      };

      request.onerror = () => reject(new Error(`Failed to clear expired cache: ${request.error?.message}`));
    });
  }

  async clearCacheByType(type: CacheType): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    const index = store.index('type');

    return new Promise((resolve, reject) => {
      const request = index.openCursor(type);

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(new Error(`Failed to clear cache by type: ${request.error?.message}`));
    });
  }

  // ============================================================================
  // 私有方法
  // ============================================================================

  private createObjectStores(db: IDBDatabase): void {
    // 书源存储
    if (!db.objectStoreNames.contains('bookSources')) {
      const bookSourceStore = db.createObjectStore('bookSources', { keyPath: 'id' });
      bookSourceStore.createIndex('name', 'name', { unique: false });
      bookSourceStore.createIndex('url', 'url', { unique: true });
      bookSourceStore.createIndex('enabled', 'enabled', { unique: false });
      bookSourceStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // 图书存储
    if (!db.objectStoreNames.contains('books')) {
      const bookStore = db.createObjectStore('books', { keyPath: 'id' });
      bookStore.createIndex('sourceId', 'sourceId', { unique: false });
      bookStore.createIndex('title', 'title', { unique: false });
      bookStore.createIndex('author', 'author', { unique: false });
      bookStore.createIndex('category', 'category', { unique: false });
      bookStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // 用户数据存储
    if (!db.objectStoreNames.contains('userData')) {
      const userDataStore = db.createObjectStore('userData', { keyPath: 'id' });
      userDataStore.createIndex('type', 'type', { unique: false });
      userDataStore.createIndex('bookId', 'bookId', { unique: false });
      userDataStore.createIndex('createdAt', 'createdAt', { unique: false });
    }

    // 缓存存储
    if (!db.objectStoreNames.contains('cache')) {
      const cacheStore = db.createObjectStore('cache', { keyPath: 'id' });
      cacheStore.createIndex('type', 'type', { unique: false });
      cacheStore.createIndex('key', 'key', { unique: false });
      cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      cacheStore.createIndex('createdAt', 'createdAt', { unique: false });
    }
  }

  private setupErrorHandling(): void {
    if (this.db) {
      this.db.onerror = (event) => {
        console.error('IndexedDB error:', event);
        this.emitEvent('DATA_CORRUPTED', { error: event });
      };

      this.db.onversionchange = () => {
        this.db?.close();
        this.db = null;
      };
    }
  }

  private async clearObjectStore(transaction: IDBTransaction, storeName: string): Promise<void> {
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to clear ${storeName}: ${request.error?.message}`));
    });
  }

  private async getCacheInfo(): Promise<{ itemCount: number; totalSize: number; expiredCount: number }> {
    if (!this.db) return { itemCount: 0, totalSize: 0, expiredCount: 0 };

    const transaction = this.db.transaction(['cache'], 'readonly');
    const store = transaction.objectStore('cache');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result || [];
        const now = new Date();
        let totalSize = 0;
        let expiredCount = 0;

        results.forEach(item => {
          totalSize += JSON.stringify(item.data).length;
          if (new Date(item.expiresAt) < now) {
            expiredCount++;
          }
        });

        resolve({
          itemCount: results.length,
          totalSize,
          expiredCount
        });
      };
      request.onerror = () => reject(new Error(`Failed to get cache info: ${request.error?.message}`));
    });
  }

  private getLocalStorageSize(): number {
    let total = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return total;
  }

  private convertToBookInfo(result: any): BookInfo {
    return {
      id: result.id,
      title: result.title,
      author: result.author,
      category: result.category,
      coverUrl: result.coverUrl,
      description: result.description,
      chapters: result.chapters,
      sourceId: result.sourceId,
      sourceUrl: '', // 需要从书源中获取
      tags: result.metadata.tags || [],
      lastChapter: result.metadata.lastChapter,
      updateTime: result.metadata.updateTime,
      wordCount: result.metadata.wordCount,
      rating: result.metadata.rating,
      status: result.metadata.status,
      language: result.metadata.language
    };
  }

  private generateUserDataId(type: UserDataType, bookId?: string): string {
    return bookId ? `${type}:${bookId}` : type;
  }

  private async deleteCacheItem(id: string): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    store.delete(id);
  }

  private emitEvent(type: StorageEventType, data?: any): void {
    const listeners = this.eventListeners.get(type) || [];
    const event: StorageEvent = {
      type,
      timestamp: new Date(),
      data
    };

    listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in storage event listener:', error);
      }
    });
  }

  // ============================================================================
  // 事件监听
  // ============================================================================

  addEventListener(type: StorageEventType, listener: Function): void {
    if (!this.eventListeners.has(type)) {
      this.eventListeners.set(type, []);
    }
    this.eventListeners.get(type)!.push(listener);
  }

  removeEventListener(type: StorageEventType, listener: Function): void {
    const listeners = this.eventListeners.get(type);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }
}